import { ConnectWalletBanner } from '@/components/banners/connect-wallet-banner';
import { BodyText } from '@/components/body-text';
import { CopyText } from '@/components/copy-text';
import { DiscordButton } from '@/components/discord-button';
import { GoonAvatar } from '@/components/goon-avatar';
import { HeaderText } from '@/components/header-text';
import { InviteFriendsModal } from '@/components/modals/invite-friends-modal';
import { DescriptionText } from '@/components/text/description';
import { TgButton } from '@/components/tg-button';
import { XButton } from '@/components/x-button';
import { useAuth } from '@/hooks/useAuth';
import { useGuardiansCampaignStats } from '@/hooks/useGuardiansCampaignStats';
import { useGuardiansCampaignUserStats } from '@/hooks/useGuardiansCampaignUserStats';
import { truncateAddress } from '@/utils';
import { Box, Card, HStack, Icon, Skeleton, Spacer, Stack, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { LottieReact } from '@lottielab/lottie-player';
import { FaLock } from 'react-icons/fa6';
import { HiMiniWallet } from 'react-icons/hi2';
import { tierIdToPresentationalData } from './constants';
import { getHighestQualifiedTier, getLowestUnqualifiedTier } from './utils';

const FullsizeProfile = ({ walletAddress }: { walletAddress: string }) => (
  <HStack justifyContent="space-between" gap={4} w="full">
    <GoonAvatar seed={walletAddress} boxSize="60px" />
    <VStack alignItems="left">
      <CopyText value={walletAddress}>
        <HeaderText color="fg">{truncateAddress(walletAddress)}</HeaderText>
      </CopyText>
      <BodyText fontSize="md">Points refresh every day at midnight UTC</BodyText>
    </VStack>
    <Spacer />
    <InviteFriendsModal />
  </HStack>
);
const MobileProfile = ({ walletAddress }: { walletAddress: string }) => (
  <>
    <HStack justifyContent="space-between" gap={4} w="full">
      <GoonAvatar seed={walletAddress} boxSize="60px" />
      <Spacer />
      <InviteFriendsModal />
    </HStack>
    <VStack alignItems="left" w="full">
      <CopyText value={walletAddress}>
        <HeaderText color="fg">{truncateAddress(walletAddress)}</HeaderText>
      </CopyText>
      <BodyText fontSize="md">Points refresh every day at midnight UTC</BodyText>
    </VStack>
  </>
);

const idToLottie = {
  tier1: 'https://cdn.lottielab.com/l/EXWQhNUhCKHKev.json',
  tier2: 'https://cdn.lottielab.com/l/Bhb7Lnfn4qKKR6.json',
  tier3: 'https://cdn.lottielab.com/l/6R6fyfJkgLhNx3.json',
  tier4: 'https://cdn.lottielab.com/l/7PFrKNVUpKGJ4m.json',
  tier5: 'https://cdn.lottielab.com/l/6cvVoqd9PZ7ApB.json',
  bonusTier: 'https://cdn.lottielab.com/l/36kLosAES86sSb.json',
};

export const GuardiansUserProfile = () => {
  const { address } = useAuth();
  const { guardiansCampaignUserStats } = useGuardiansCampaignUserStats();
  const { guardiansCampaignStats } = useGuardiansCampaignStats();
  const { txCount = 0, rank } = guardiansCampaignUserStats ?? {};
  const currentTier =
    guardiansCampaignUserStats && guardiansCampaignStats
      ? getHighestQualifiedTier(guardiansCampaignUserStats, guardiansCampaignStats)
      : null;
  const showFullsize = useBreakpointValue({
    base: false,
    sm: false,
    md: true,
  });

  if (!address)
    return (
      <ConnectWalletBanner bodyText="Join the Guardians of Plume campaign to earn rewards, unlock badges, and track your progress." />
    );
  return (
    <Stack w="full" direction={['column', 'column', 'column', 'column', 'row']} gap={6}>
      {currentTier && (
        <Card.Root rounded="2xl" overflow="hidden" w={['full', 'full', 'full', 'full', 372]} position="relative">
          <Box position="absolute" right={4} top={4} h={125} w={120}>
            <LottieReact src={idToLottie[currentTier.id]} />
          </Box>
          <Card.Header
            bgColor="#2A2C45"
            h={84}
            gap={0}
            pt={4}
            bgImage="url(/assets/guardians/stars.avif)"
            bgSize="contain"
            bgPos="center"
          >
            <BodyText fontSize="md" color="#FFFFFF99">
              {tierIdToPresentationalData[currentTier.id].tierLevel}
            </BodyText>
            <HeaderText fontSize="xl" color="fg.inverted">
              {tierIdToPresentationalData[currentTier.id].description}
            </HeaderText>
          </Card.Header>
          <Card.Body pt={8}>
            {currentTier.tx ? (
              <>
                <HeaderText fontSize="40px">{currentTier.tx}+</HeaderText>
                <BodyText>Transactions</BodyText>
              </>
            ) : (
              <>
                <BodyText>Rank</BodyText>
                <HeaderText fontSize="40px">{rank}</HeaderText>
              </>
            )}
            <BodyText fontSize="md" mt={6}>
              You made a total of{' '}
              <Text as="span" color="fg">
                {txCount} transactions
              </Text>
            </BodyText>
          </Card.Body>
        </Card.Root>
      )}
      <Card.Root rounded="2xl" flex="1">
        <Card.Body>
          <VStack gap={6} w="full" justifyContent="space-between" h="full">
            {showFullsize ? <FullsizeProfile walletAddress={address} /> : <MobileProfile walletAddress={address} />}
            <FlavorCard />
          </VStack>
        </Card.Body>
      </Card.Root>
    </Stack>
  );
};

const FlavorCard = () => {
  const { guardiansCampaignUserStats, guardiansCampaignUserPending, guardiansCampaignUserIsError } =
    useGuardiansCampaignUserStats();
  const { guardiansCampaignStats, guardiansCampaignPending, guardiansCampaignIsError } = useGuardiansCampaignStats();
  const pendingStats = guardiansCampaignUserPending || guardiansCampaignPending;
  const errorRetrievingStats = guardiansCampaignIsError || guardiansCampaignUserIsError;

  const { campaignComplete } = guardiansCampaignStats ?? {};
  const { txCount = 0 } = guardiansCampaignUserStats ?? {};

  const lowestUnqualifiedTier =
    guardiansCampaignUserStats && guardiansCampaignStats
      ? getLowestUnqualifiedTier(guardiansCampaignUserStats, guardiansCampaignStats)
      : null;

  const txToNextTier = (lowestUnqualifiedTier?.tx ?? 0) - txCount;
  if (errorRetrievingStats) return null;
  if (!campaignComplete)
    return (
      <Skeleton w="full" loading={pendingStats}>
        <Card.Root bgColor="#5A3CE80D" rounded="2xl" w="full">
          <Card.Body p={4}>
            <HStack h="full">
              <Icon boxSize={6} m={2} color="#272459">
                <HiMiniWallet />
              </Icon>
              <Box>
                <DescriptionText color="fg" fontSize="md">
                  {lowestUnqualifiedTier
                    ? `You're ${txToNextTier} transactions away from ${tierIdToPresentationalData[lowestUnqualifiedTier.id].tierLevel}`
                    : 'Tier 5 Completed'}
                </DescriptionText>
                <DescriptionText fontSize="md">
                  {lowestUnqualifiedTier
                    ? `Reach ${lowestUnqualifiedTier?.tx} total transactions to level up and unlock rewards`
                    : 'Keep it up! The top 100 rank unlock the Bonus Tier when campaign ends'}
                </DescriptionText>
              </Box>
            </HStack>
          </Card.Body>
        </Card.Root>
      </Skeleton>
    );

  return (
    <Skeleton w="full" loading={pendingStats}>
      <Card.Root bgColor="bg.muted" rounded="2xl" w="full">
        <Card.Body p={4}>
          <HStack h="full">
            <Icon boxSize={5} m={2} color="fg.muted">
              <FaLock />
            </Icon>

            <VStack flex="1" alignItems="left" gap={0}>
              <DescriptionText color="fg">Campaign is now over</DescriptionText>
              <DescriptionText>Keep an eye on our socials for future campaigns</DescriptionText>
            </VStack>
            <HStack gap={0}>
              <XButton />
              <DiscordButton />
              <TgButton />
            </HStack>
          </HStack>
        </Card.Body>
      </Card.Root>
    </Skeleton>
  );
};
